import React, { useState, useMemo, useEffect, useCallback } from 'react'
import styled from 'styled-components'
import { Checkbox, Button, CircularProgress, TextField } from '@mui/material'
import Modal from 'components/atoms/Modal'
import { fetchUserBps } from 'services/BpService'

const BpPicker = ({ open, onClose, onSubmit, existingBpIds = [] }) => {
  // Fetch all BPs in the picker via local state — the global redux duck only
  // ever holds the currently-paginated page (entries replaces on each fetch),
  // so it can't be used as a "complete user BPs" source. Loop pages here.
  const [allBps, setAllBps] = useState([])
  const [fetching, setFetching] = useState(false)
  const [selected, setSelected] = useState([])
  const [search, setSearch] = useState('')

  useEffect(() => {
    if (!open) return
    let cancelled = false
    const loadAllPages = async () => {
      setFetching(true)
      try {
        const accumulated = []
        let page = 1
        const MAX_PAGES = 100
        while (page <= MAX_PAGES) {
          const res = await fetchUserBps({ page, sort: '-created_at' })
          const data = res?.data?.data || []
          accumulated.push(...data)
          const lastPage = res?.data?.meta?.lastPage ?? page
          if (page >= lastPage || data.length === 0) break
          page += 1
        }
        if (!cancelled) setAllBps(accumulated)
      } catch (e) {
        if (!cancelled) setAllBps([])
      } finally {
        if (!cancelled) setFetching(false)
      }
    }
    loadAllPages()
    return () => { cancelled = true }
  }, [open])

  const available = useMemo(() =>
    allBps
      .filter(bp => bp.status === 'active' && !existingBpIds.includes(bp.id))
      .filter(bp => !search || bp.name?.toLowerCase().includes(search.toLowerCase()))
      .sort((a, b) => (a.name || '').localeCompare(b.name || '')),
    [allBps, existingBpIds, search]
  )

  // Count buildings that exist but are excluded because they're not active
  // (drafts, archived, etc.) — used to show a clear explanatory note.
  const inactiveCount = useMemo(
    () => allBps.filter(bp => bp.status !== 'active' && !existingBpIds.includes(bp.id)).length,
    [allBps, existingBpIds]
  )

  const toggle = (id) => {
    setSelected(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id])
  }

  const handleSubmit = () => {
    onSubmit(selected)
    setSelected([])
    setSearch('')
  }

  const handleClose = useCallback(() => {
    setSelected([])
    setSearch('')
    onClose()
  }, [onClose])

  return (
    <Modal open={open} onClose={handleClose} title="Add Building Passports" maxWidth="sm" fullWidth>
      <PickerContainer>
        <Hint>
          Only active Building Passports can be added to a portfolio.
          {!fetching && inactiveCount > 0 && (
            <> {inactiveCount} of your Building Passport{inactiveCount === 1 ? ' is' : 's are'} hidden because {inactiveCount === 1 ? 'it is' : 'they are'} not active.</>
          )}
        </Hint>
        <TextField
          placeholder="Search active Building Passports…"
          value={search}
          onChange={e => setSearch(e.target.value)}
          fullWidth
          size="small"
          autoFocus
        />
        <BpList>
          {fetching && allBps.length === 0 ? (
            <EmptyMsg>
              <CircularProgress size={18} sx={{ color: '#D24724', mr: 1, verticalAlign: 'middle' }} />
              Loading your Building Passports…
            </EmptyMsg>
          ) : available.length === 0 ? (
            <EmptyMsg>{search ? 'No matching Building Passports' : 'All your Building Passports are already in this portfolio'}</EmptyMsg>
          ) : available.map((bp, index) => (
            <BpRow key={bp.id} data-tour-id={index === 0 ? 'bpPicker.first-row' : undefined} onClick={() => toggle(bp.id)}>
              <Checkbox checked={selected.includes(bp.id)} size="small" />
              <BpInfo>
                <BpName>{bp.name}</BpName>
                {bp.street && <BpAddr>{bp.street}, {bp.city}</BpAddr>}
              </BpInfo>
            </BpRow>
          ))}
        </BpList>
        <Actions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button variant="contained" data-tour-id='bpPicker.submit' onClick={handleSubmit} disabled={selected.length === 0}>
            Add {selected.length} Building Passport{selected.length !== 1 ? 's' : ''}
          </Button>
        </Actions>
      </PickerContainer>
    </Modal>
  )
}

export default BpPicker

const PickerContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 8px 0;
`

const BpList = styled.div`
  max-height: 400px;
  overflow-y: auto;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
`

const BpRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  cursor: pointer;
  border-bottom: 1px solid #f3f4f6;

  &:last-child { border-bottom: none; }
  &:hover { background: #f9fafb; }
`

const BpInfo = styled.div`
  min-width: 0;
`

const BpName = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: #111827;
`

const BpAddr = styled.div`
  font-size: 12px;
  color: #9ca3af;
`

const EmptyMsg = styled.div`
  padding: 24px;
  text-align: center;
  font-size: 13px;
  color: #9ca3af;
`

const Hint = styled.div`
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 10px 12px;
  font-size: 12px;
  line-height: 1.45;
  color: #475569;
`

const Actions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
`
